import "./Accordion.css";
import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  createContext,
  ReactNode,
  HTMLAttributes,
  forwardRef,
  Ref,
} from "react";
import ExpandChevron from "./ExpandChevron";
import styled from "styled-components";
import { useTracking } from "../Tracking";

interface IAccordionContext {
  open: HTMLDivElement | null;
  toggle: (row: HTMLDivElement) => void;
}

const AccordionContext = createContext<IAccordionContext | null>(null);

const Accordion = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const [open, setOpen] = useState<HTMLDivElement | null>(null);

  const context = {
    open: open,
    toggle: (row: HTMLDivElement) =>
      setOpen((open) => (open === row ? null : row)),
  };

  return (
    <AccordionContext.Provider value={context}>
      <div className={["Accordion", className].filter((c) => !!c).join(" ")}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

const StandardAccordionComponent = styled.span`
  flex: 1 1 auto;
  margin: 0;
  ${({ theme }) => theme.fontStyle.article.bodyHeader};
`;

type AccordionComponentType = "h3" | "standard";

interface AccordionTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  component?: AccordionComponentType;
  title: string;
}

const AccordionTitle = forwardRef<
  HTMLHeadingElement | null,
  AccordionTitleProps
>(({ title, component = "standard", ...props }, ref) => {
  switch (component) {
    case "h3":
      return (
        <h3 ref={ref} {...props}>
          {title}
        </h3>
      );
    case "standard":
      return (
        <StandardAccordionComponent ref={ref} {...props}>
          {title}
        </StandardAccordionComponent>
      );
    default:
      throw new Error(`Unrecognized titleComponent "${component}".`);
  }
});

export function normalizedAccordionId(title: string) {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/æ/g, "a")
    .replace(/ø/g, "o")
    .replace(/å/g, "a")
    .replace(/[^-a-z0-9]/g, "");
}

const Expand = styled(ExpandChevron)`
  width: 1em;
  height: auto;

  & > rect {
    fill: #adbbda;
  }
`;

interface AccordionRowProps {
  title: string;
  children: ReactNode;
  className?: string;
  open?: boolean;
  id: string | number;
  titleRef?: Ref<HTMLHeadingElement | null>;
  titleComponent?: AccordionComponentType;
  onOpen: () => void;
  openEvent: {
    category: string;
    action: string;
    label?: string;
    value?: string;
  } | null;
}

const AccordionRow = ({
  title,
  children,
  className,
  open,
  id,
  titleRef,
  titleComponent = "standard",
  onOpen,
  openEvent = null,
}: AccordionRowProps) => {
  const context = useContext(AccordionContext);
  if (!context) throw new Error("No AccordionContext found!");

  const tracking = useTracking();
  const ref = useRef<HTMLDivElement | null>(null);

  const isOpen =
    open || (context.open && context.open === ref.current) || false;

  useEffect(() => {
    if (onOpen && isOpen) onOpen();
  }, [isOpen, onOpen]);

  const titleId = (id || normalizedAccordionId(title)).toString();

  return (
    <div
      className={["Accordion-row", className].filter((c) => !!c).join(" ")}
      ref={ref}
    >
      <div className="Accordion-row-header-wrapper">
        <a
          className="Accordion-row-header"
          href={`#${titleId}`}
          onClick={(event) => {
            event.preventDefault();
            if (ref.current) context.toggle(ref.current);
            if (!isOpen && openEvent) {
              tracking.event(openEvent);
            }
          }}
        >
          <AccordionTitle
            ref={titleRef}
            id={titleId}
            title={title}
            component={titleComponent}
          />
          <div className="Accordion-row-indicator">
            <Expand open={isOpen} />
          </div>
        </a>
      </div>
      <div
        className="Accordion-row-body"
        id={titleId}
        style={{ display: isOpen ? "block" : "none" }}
      >
        {children}
      </div>
    </div>
  );
};

Accordion.Row = AccordionRow;

export default Accordion;
